import {
  BasicButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  GhostButton,
  Modal,
  ModalTrigger,
  PrimaryButton,
} from "@certn/cascadia";
import { CascadiaJsonFormContext } from "@certn/forms";
import {
  CascadiaJsonForm,
  CascadiaJsonFormContent,
} from "@certn/forms/src/CascadiaJsonForm";
import { renderers } from "@certn/forms/src/renderers";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { ArrayModalControlContext } from "./ArrayModalControlContext";

interface Props {
  /** The title of the dialog. */
  title: string;

  /** The path to the item being managed. */
  itemPath: string;

  /** The initial values for the form. */
  initialValues?: unknown;

  /** A function that renders the trigger element for the modal. */
  trigger: (props: { toggle: () => void }) => React.ReactNode;

  /** A callback function invoked when the form is submitted. */
  onSubmit: (itemPath: string, values: unknown) => Promise<void>;

  /** An optional callback function invoked when the delete action is triggered. */
  onDelete?: (itemPath: string) => Promise<void>;
}

/**
 * A dialog component for managing array items within a modal.
 * This component is intended to be used with {@link ArrayModalControl}.
 */
export function ArrayModalControlDialog(props: Props) {
  const { initialValues, trigger, itemPath } = props;
  const { t } = useTranslation();
  const context = useContext(ArrayModalControlContext);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useContext(CascadiaJsonFormContext);
  const { label, childSchema, childUiSchema, data, testIdPrefix } = context;

  return (
    <ModalTrigger trigger={trigger}>
      {({ close }) => (
        <Modal testID="application--action-list-control--item-modal">
          <Dialog>
            <CascadiaJsonForm
              initialValues={initialValues}
              schema={childSchema}
              uischema={childUiSchema}
              renderers={form.renderers || renderers}
              parentValues={data}
              formID={form.formID}
              onSubmit={async (values) => {
                try {
                  setIsSubmitting(true);
                  await props.onSubmit(itemPath, values);
                  close();
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              <DialogHeader>{props.title}</DialogHeader>
              <DialogContent>
                <CascadiaJsonFormContent />
              </DialogContent>
              <DialogFooter
                leftContent={
                  props.onDelete ? (
                    <GhostButton
                      testID={`${testIdPrefix}-delete-button`}
                      onPress={async () => {
                        await props.onDelete?.(itemPath);
                        close();
                      }}
                    >
                      {t("Delete")}
                    </GhostButton>
                  ) : undefined
                }
              >
                <BasicButton
                  testID={`${testIdPrefix}-cancel-button`}
                  onPress={close}
                >
                  {t("Cancel")}
                </BasicButton>
                <PrimaryButton
                  type="submit"
                  variant="brand-primary"
                  isLoading={isSubmitting}
                  testID={`${testIdPrefix}-save-button`}
                >
                  {t("Save {{label}}", { label })}
                </PrimaryButton>
              </DialogFooter>
            </CascadiaJsonForm>
          </Dialog>
        </Modal>
      )}
    </ModalTrigger>
  );
}
